import type {
  Asset2CampaignGqlView,
  Asset2ReviewStatus,
  Asset2TranscodeInfo,
  CountryCode,
  DisplayAsset2,
  TextAsset2,
  VideoAsset2,
} from '../apis/graphql';
import { Assets2SubclassTypes } from '../apis/graphql';
import type { Asset2, Asset2Display, Asset2Extended, Asset2GQL, Asset2Text, Asset2Video, Nullable } from '../models';
import { deriveAssetReviewForCountryList } from './reviewDerivation';

export const isTextAsset = (asset: TextAsset2 | VideoAsset2 | DisplayAsset2): asset is TextAsset2 => {
  return asset.subClass === Assets2SubclassTypes.TextAsset;
};

export const isDisplayAsset = (asset: TextAsset2 | VideoAsset2 | DisplayAsset2): asset is DisplayAsset2 => {
  return asset.subClass === Assets2SubclassTypes.DisplayAsset;
};

export const isVideoAsset = (asset: TextAsset2 | VideoAsset2 | DisplayAsset2): asset is VideoAsset2 => {
  return asset.subClass === Assets2SubclassTypes.VideoAsset;
};

export const isExtendedVideoAsset = (asset: Asset2Extended | null): asset is Asset2Video => {
  return asset?.subClass === Assets2SubclassTypes.VideoAsset;
};

export const isExtendedDisplayAsset = (asset: Asset2Extended | null): asset is Asset2Display => {
  return asset?.subClass === Assets2SubclassTypes.DisplayAsset;
};

export const isExtendedTextAsset = (asset: Asset2Extended | null): asset is Asset2Text => {
  return asset?.subClass === Assets2SubclassTypes.TextAsset;
};

export const convertExtendedAssetToGQLView = (asset?: Asset2 | null): Asset2CampaignGqlView | null => {
  if (!asset) return null;

  let transcode: Asset2TranscodeInfo | null = null;
  let text: string | null = null;

  if (isExtendedVideoAsset(asset) && asset.transcode) {
    transcode = asset.transcode;
  }

  if (isExtendedTextAsset(asset) && asset.text) {
    text = asset.text;
  }

  return {
    id: asset.id,
    countryReviews: asset.countryReviews,
    transcode,
    assetTagIds: asset.assetTagList.map(({ id }) => id),
    assetTagList: asset.assetTagList,
    subClass: asset.subClass,
    name: asset.name,
    createdAt: asset.createdAt,
    lastSeenAt: asset.lastSeenAt,
    text: text,
    language: asset.language,
  };
};

export const transformAsset2GQLToAsset2 = (
  asset: Asset2GQL,
  publisherList?: Nullable<string[]>,
  countriesList?: string[]
): Asset2 => {
  const countryReviews = asset.countryReviews ?? [];

  const { review, rejectionReasons, approvedPublishers } = deriveAssetReviewForCountryList(
    countryReviews,
    countriesList ?? [],
    publisherList
  );

  // Build countryCodes from reviews (fallback to asset.countryCodes)
  const countryCodes = countryReviews.length
    ? countryReviews
        .map((countryReview) => countryReview.country)
        .filter((country): country is CountryCode => Boolean(country))
    : (asset.countryCodes ?? []).filter((country): country is CountryCode => country != null);

  const asset2Base = {
    id: asset.id,
    name: asset.name,
    md5: asset.md5,
    s3Url: asset.s3Url,
    assetTagList: asset.assetTagList ?? [],
    assetSources: asset.assetSources ?? [],
    countryReviews: asset.countryReviews,
    createdAt: asset.createdAt,
    updatedAt: asset.updatedAt,
    lastSeenAt: asset.lastSeenAt,
    filename: asset.filename ?? asset.name,
    countryCodes,
    approvedPublishers,
    review: review as Asset2ReviewStatus,
    rejectionReasons,
    language: asset.language,
  };

  if (isTextAsset(asset)) {
    return {
      ...asset2Base,
      subClass: Assets2SubclassTypes.TextAsset,
      text: asset.text,
    };
  }

  if (isDisplayAsset(asset)) {
    return {
      ...asset2Base,
      subClass: Assets2SubclassTypes.DisplayAsset,
      ripcut: asset.ripcut,
      height: asset.height,
      kinko: asset.kinko,
      width: asset.width,
    };
  }

  return {
    ...asset2Base,
    subClass: Assets2SubclassTypes.VideoAsset,
    transcode: asset.transcode,
    priority: asset.priority,
    videoMetadata: asset.videoMetadata,
    audioMetadata: asset.audioMetadata,
  };
};
